// Lightbox neighbor source — lets /asset/{id} navigate prev/next AND
// page forward through the SAME list the user was browsing.
//
// The problem this solves: the lightbox unmounts the grid that opened it
// (sibling routes under AppLayout), so the grid's `fetchNextPage` dies and
// the lightbox can only see whatever pages were already in the query cache.
// At the end of that loaded window `next` is null and the forward `>`
// arrow vanishes even though the server has more photos.
//
// Fix: each grid (Library / SharedWithMe / AlbumDetail) registers the
// infinite-query options it's driving. The lightbox re-runs that same
// query (same key → shares the cache, no refetch) and owns its own
// `fetchNextPage`, so it can extend the list on demand. Registration is a
// plain module slot (not React state): it's set while the grid is mounted,
// read once by the lightbox on mount, and survives the route swap. Deep
// links / fresh tabs have no source → the lightbox falls back to scanning
// the query cache for any list that contains the asset (no pagination).

import type { AssetDTO, AssetListResponse } from '../api/types';

export interface LightboxSource {
  /// EXACT query key the grid used, so the lightbox shares its cache.
  queryKey: readonly unknown[];
  initialPageParam: string | null;
  queryFn: (ctx: { pageParam: string | null }) => Promise<AssetListResponse>;
  getNextPageParam: (lastPage: AssetListResponse) => string | null | undefined;
  /// Post-filter the grid applied client-side (e.g. Favorites in Library),
  /// so the lightbox's neighbor order matches exactly what was on screen.
  clientFilter?: (a: AssetDTO) => boolean;
}

let source: LightboxSource | null = null;

export function setLightboxSource(s: LightboxSource): void {
  source = s;
}

export function getLightboxSource(): LightboxSource | null {
  return source;
}
